export type SchemaNode = Record<string, unknown>

export interface Resolved {
  schema: SchemaNode
  refs: string[]
  // Reached a definition already on its own path: walking into it again would never end.
  cyclic: boolean
}

export const isPlainObject = (value: unknown): value is SchemaNode =>
  !!value && typeof value === 'object' && !Array.isArray(value)

export const asNode = (value: unknown): SchemaNode => (isPlainObject(value) ? value : {})

export const asText = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value)

const LOCAL_REF = /^#\/(\$defs|definitions)\/(.+)$/

export const resolve = (root: SchemaNode, node: SchemaNode, refs: string[]): Resolved => {
  let schema = node
  let trail = refs
  while (typeof schema.$ref === 'string') {
    const ref = schema.$ref
    const match = LOCAL_REF.exec(ref)
    const definitions = asNode(match && root[match[1]])
    const name = match?.[2].replace(/~1/g, '/').replace(/~0/g, '~')
    if (!name || !Object.hasOwn(definitions, name)) break
    const { $ref: _ref, ...siblings } = schema
    schema = { ...asNode(definitions[name]), ...siblings }
    if (trail.includes(ref)) return { schema, refs: trail, cyclic: true }
    trail = [...trail, ref]
  }
  return { schema, refs: trail, cyclic: false }
}

// ACI's file input. The builder gives a link and agent-runner downloads it into these parts, so
// it is one input, never a group.
export const isFile = ({ schema }: Resolved) =>
  schema.type === 'object' && Object.hasOwn(asNode(schema.properties), 'content_base64')

// ACI drops hidden inputs before the console sees a schema, so a group can arrive with an empty
// `properties`; it is still a group that yields no fields, never a JSON input of its own.
export const isContainer = (resolved: Resolved) =>
  !resolved.cyclic &&
  resolved.schema.type === 'object' &&
  isPlainObject(resolved.schema.properties) &&
  !isFile(resolved)

export const titleOf = (schema: SchemaNode) =>
  typeof schema.title === 'string' && schema.title.trim() ? schema.title : undefined

export const requiredList = (schema: SchemaNode): unknown[] =>
  Array.isArray(schema.required) ? schema.required : []

// JSON Schema allows a list, e.g. ['integer', 'null'], and a union under anyOf / oneOf.
export const typesOf = (root: SchemaNode, { schema, refs }: Resolved): string[] => {
  const own = ([] as unknown[])
    .concat(schema.type)
    .filter((type): type is string => typeof type === 'string')
  const branches = [schema.anyOf, schema.oneOf].flatMap((list) => (Array.isArray(list) ? list : []))
  return own.concat(
    branches.flatMap((branch) => {
      const resolved = resolve(root, asNode(branch), refs)
      return resolved.cyclic ? [] : typesOf(root, resolved)
    }),
  )
}

// A null member is how a schema says "may be unset"; an empty selection already says that.
export const enumValues = (schema: SchemaNode) =>
  (Array.isArray(schema.enum) ? schema.enum : []).filter((value) =>
    ['string', 'number', 'boolean'].includes(typeof value),
  )
